// Unlike the Google Tag Manager, the Posthog Client does not have a built-in way to

import { getCart } from "src/checkout/hooks/useCart"
import { CheckoutStep } from "src/checkout/state/checkoutStageStore"
import { Ccm19EmbeddingName } from "src/integrations/ccm19/embeddings"
import { getCcm19EmbeddingAccepted } from "src/integrations/ccm19/useConsented"
import { posthogBrowser } from "src/integrations/posthog/client-browser"
import { posthogPublicWriteKey } from "src/integrations/posthog/constants"
import { mapToPosthogLineItem, mapToPosthogOrder } from "src/integrations/posthog/mapping"
import { posthogRewriteBasePath } from "src/integrations/posthog/rewrites"
import { getUserId } from "src/tracking/state"
import { TrackingLineItem, TrackingOrder } from "src/tracking/types"

// handle events that are fired before the client is initialized.
let initialized = false
const eventsBeforeInit: Parameters<typeof posthogBrowser.capture>[] = []

function posthogDebug(...args: unknown[]) {
  console.debug("🦔 posthog:", ...args)
}

const posthogCapture: typeof posthogBrowser.capture = (eventName, properties) => {
  if (!initialized) {
    posthogDebug("not yet initialized, queueing event for later", eventName, properties)
    eventsBeforeInit.push([eventName, properties])
  } else {
    posthogDebug(eventName, properties)
    return posthogBrowser.capture(eventName, properties)
  }
}

export function posthogInit() {
  initialized = true

  const consented = getCcm19EmbeddingAccepted(Ccm19EmbeddingName.Posthog)
  const options: Parameters<typeof posthogBrowser.init>[1] = {
    // https://posthog.com/docs/libraries/js#config
    api_host: posthogRewriteBasePath,
    ui_host: "https://eu.posthog.com",
    person_profiles: "identified_only",
    opt_out_capturing_by_default: !consented,
    opt_out_persistence_by_default: !consented,
    capture_pageview: false,
    capture_pageleave: true,

    // https://posthog.com/docs/libraries/js#redacting-information-in-events
    before_send: (event) => {
      if (!event) {
        return null
      }
      return event
    },
  }

  posthogDebug("init", options)
  posthogBrowser.init(posthogPublicWriteKey, options)
  posthogBrowser.identify(getUserId())
  window.posthog = posthogBrowser

  // Send all events that were pushed before the pixel was initialized.
  if (eventsBeforeInit.length) {
    for (const parameters of eventsBeforeInit) {
      posthogCapture(...parameters)
    }
    eventsBeforeInit.length = 0
  }
}

// =====================================
// Built-In Events
// https://posthog.com/docs/product-analytics/autocapture#autocaptured-events
// =====================================

// https://posthog.com/docs/libraries/next-js#capturing-pageviews
export function posthogTrackPageView(data: { referrer: string; pathname: string }) {
  posthogCapture("$pageview", {
    ...data,
    $current_url: window.location.href,
  })
}

// =====================================
// Ecommerce Events
// https://posthog.com/docs/data/event-spec/ecommerce-events
// =====================================

// https://posthog.com/docs/data/event-spec/ecommerce-events#products-searched
export function posthogTrackProductsSearched(data: { input: string; listId: string }) {
  posthogCapture("Products Searched", {
    query: data.input,

    // custom
    list_id: data.listId,
  })
}

// https://posthog.com/docs/data/event-spec/ecommerce-events#product-added
export function posthogTrackProductAdded(item: TrackingLineItem) {
  posthogCapture("Product Added", mapToPosthogLineItem(item))
}

// https://posthog.com/docs/data/event-spec/ecommerce-events#coupon-applied
export function posthogTrackCouponApplied(data: { code: string }) {
  posthogCapture("Coupon Applied", {
    coupon_id: data.code,
  })
}

function mapStep(step: CheckoutStep) {
  switch (step) {
    case CheckoutStep.Cart:
      return "cart"
    case CheckoutStep.ShippingInfo:
      return "shipping"
    case CheckoutStep.PaymentInfo:
      return "payment"
    case CheckoutStep.ExpressCheckoutCompletion:
      return "express_summary"
    case CheckoutStep.OrderComplete:
      return "thankyou"
    default:
      return "unknown"
  }
}

// https://posthog.com/docs/data/event-spec/ecommerce-events#checkout-step-viewed
export function posthogTrackCheckoutStepViewed(step: CheckoutStep) {
  posthogCapture("Checkout Step Viewed", {
    step: step,
    shipping_method: getCart()?.shipping_rates.at(0)?.name,
    payment_method: getCart()?.payment_methods.at(0),

    // custom
    step_id: mapStep(step),
  })
}

// https://posthog.com/docs/data/event-spec/ecommerce-events#order-completed
export function posthogTrackOrderCompleted(order: TrackingOrder) {
  posthogCapture("Order Completed", mapToPosthogOrder(order))
}

// =====================================
// Custom Events
// https://posthog.com/docs/getting-started/send-events
// =====================================

export function posthogTrackEmailSignup(data: { name: string; listId: string }) {
  posthogCapture("signup", {
    name: data.name,
    list_id: data.listId,
  })
}
